// Single source of truth for every CMS-editable UI string that ISN'T part
// of a page's block layout (see blockRegistry.js for that) — buttons,
// validation/error messages, instructional copy, empty states, etc. on the
// public-facing site. Each entry's `defaultValue` is both what ships
// before an admin ever touches it AND the fallback if a key is somehow
// missing from Firestore — the two can never drift since useSiteText.js
// reads defaultValue from here rather than taking a fallback per call
// site.
//
// Adding a new editable string: add a key here (grouped by the flow/page
// it lives on), then call useSiteText("that.key") wherever the literal
// currently is. Not meant for admin-panel-only strings (create-*,
// manage-bookings, Edit Site itself) — those stay hardcoded by design.
export const TEXT_REGISTRY = {
  // --- Auth gate modal, profile step (src/components/AuthGateModal.jsx) ---
  "auth.profile.eyebrow": {
    label: "Eyebrow",
    group: "Auth Modal — Profile Step",
    defaultValue: "One last step",
  },
  "auth.profile.title": {
    label: "Title",
    group: "Auth Modal — Profile Step",
    defaultValue: "Complete your profile",
  },
  "auth.profile.subtext": {
    label: "Subtext",
    group: "Auth Modal — Profile Step",
    defaultValue:
      "We need a few travel details before confirming your booking.",
  },
  "auth.profile.subtextEmailNote": {
    label: "Subtext — email verification note",
    group: "Auth Modal — Profile Step",
    defaultValue:
      "We've also sent a verification link to your email — no need to act on it now.",
  },
  "auth.profile.sectionContact": {
    label: "Section label — Contact",
    group: "Auth Modal — Profile Step",
    defaultValue: "Contact",
  },
  "auth.profile.sectionPassport": {
    label: "Section label — Passport",
    group: "Auth Modal — Profile Step",
    defaultValue: "Passport",
  },
  "auth.profile.fieldFullName": {
    label: "Field label — full name",
    group: "Auth Modal — Profile Step",
    defaultValue: "Full Name (as on passport)",
  },
  "auth.profile.fieldFullNamePlaceholder": {
    label: "Field placeholder — full name",
    group: "Auth Modal — Profile Step",
    defaultValue: "Full name exactly as on passport",
  },
  "auth.profile.fieldPassportNumber": {
    label: "Field label — passport number",
    group: "Auth Modal — Profile Step",
    defaultValue: "Passport Number",
  },
  "auth.profile.fieldPassportNumberPlaceholder": {
    label: "Field placeholder — passport number",
    group: "Auth Modal — Profile Step",
    defaultValue: "e.g., A12345678",
  },
  "auth.profile.fieldNationality": {
    label: "Field label — nationality",
    group: "Auth Modal — Profile Step",
    defaultValue: "Nationality",
  },
  "auth.profile.fieldBirthCountry": {
    label: "Field label — birth country",
    group: "Auth Modal — Profile Step",
    defaultValue: "Birth Country",
  },
  "auth.profile.fieldBatamLocal": {
    label: "Field label — Batam Local question",
    group: "Auth Modal — Profile Step",
    defaultValue: "Are you a Batam Local with KTP?",
  },
  "auth.profile.ktpUploadInstruction": {
    label: "KTP upload instruction",
    group: "Auth Modal — Profile Step",
    defaultValue: "Please upload your KTP for verification",
  },
  "auth.profile.fieldKtpFront": {
    label: "Field label — KTP front",
    group: "Auth Modal — Profile Step",
    defaultValue: "KTP Front View",
  },
  "auth.profile.fieldKtpBack": {
    label: "Field label — KTP back",
    group: "Auth Modal — Profile Step",
    defaultValue: "KTP Back View",
  },
  "auth.profile.ktpFileConstraints": {
    label: "KTP file constraints note",
    group: "Auth Modal — Profile Step",
    defaultValue: "JPEG, PNG, WEBP or PDF. 5MB total across both files.",
  },
  "auth.profile.fieldGender": {
    label: "Field label — gender",
    group: "Auth Modal — Profile Step",
    defaultValue: "Gender",
  },
  "auth.profile.fieldDateOfBirth": {
    label: "Field label — date of birth",
    group: "Auth Modal — Profile Step",
    defaultValue: "Date of Birth",
  },
  "auth.profile.fieldDateOfIssue": {
    label: "Field label — passport date of issue",
    group: "Auth Modal — Profile Step",
    defaultValue: "Passport Date of Issue",
  },
  "auth.profile.fieldExpiryDate": {
    label: "Field label — passport expiry date",
    group: "Auth Modal — Profile Step",
    defaultValue: "Passport Expiry Date",
  },
  "auth.profile.savingLabel": {
    label: "Save button — saving state",
    group: "Auth Modal — Profile Step",
    defaultValue: "Saving...",
  },
  "auth.profile.saveButton": {
    label: "Save button",
    group: "Auth Modal — Profile Step",
    defaultValue: "Save & Continue",
  },

  // --- Auth gate modal, profile step validation errors ---
  "auth.error.mobileRequired": {
    label: "Error — mobile number missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please fill in your mobile number",
  },
  "auth.error.mobileInvalid": {
    label: "Error — mobile number invalid",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Mobile number must contain numbers only",
  },
  "auth.error.fullNameRequired": {
    label: "Error — full name missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please fill in your name as on passport",
  },
  "auth.error.fullNameInvalid": {
    label: "Error — full name invalid",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Special characters and numbers are not allowed",
  },
  "auth.error.passportNumberRequired": {
    label: "Error — passport number missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please fill in your passport number",
  },
  "auth.error.passportNumberInvalid": {
    label: "Error — passport number invalid",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Special characters are not allowed",
  },
  "auth.error.nationalityRequired": {
    label: "Error — nationality missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please select your nationality",
  },
  "auth.error.birthCountryRequired": {
    label: "Error — birth country missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please select your birth country",
  },
  "auth.error.batamLocalRequired": {
    label: "Error — Batam Local answer missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please indicate if you are a Batam Local with KTP Batam",
  },
  "auth.error.ktpFrontRequired": {
    label: "Error — KTP front missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please upload the front view of your KTP card",
  },
  "auth.error.ktpBackRequired": {
    label: "Error — KTP back missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please upload the back view of your KTP card",
  },
  "auth.error.genderRequired": {
    label: "Error — gender missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please choose your gender",
  },
  "auth.error.dateOfBirthRequired": {
    label: "Error — date of birth missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please select your date of birth",
  },
  "auth.error.dateOfBirthPast": {
    label: "Error — date of birth not in the past",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Date of birth must be in the past",
  },
  "auth.error.dateOfIssueRequired": {
    label: "Error — passport date of issue missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please select your passport date of issue",
  },
  "auth.error.expiryRequired": {
    label: "Error — passport expiry missing",
    group: "Auth Modal — Validation Errors",
    defaultValue: "Please select your passport date of expiry",
  },
  "auth.error.expiryTooFar": {
    label: "Error — passport expiry too far from issue date",
    group: "Auth Modal — Validation Errors",
    defaultValue:
      "Passport expiry date must not be more than 10 years from the date of issue",
  },

  // --- Package/round selection (src/pages/PackageDetail.jsx) ---
  // "{round}"/"{course}" are filled in via fillTemplate() below — keep the
  // token if you edit these, or the round number/course name won't appear.
  "booking.instructionsHeading": {
    label: "Instructions box — heading",
    group: "Booking Flow — Round Selection",
    defaultValue: "Instructions",
  },
  "booking.stepSelectDate": {
    label: "Instructions box — step 1",
    group: "Booking Flow — Round Selection",
    defaultValue: "Select a date for Round {round}",
  },
  "booking.stepSelectCourse": {
    label: "Instructions box — step 2",
    group: "Booking Flow — Round Selection",
    defaultValue: "Select the course for Round {round}",
  },
  "booking.stepSelectTime": {
    label: "Instructions box — step 3",
    group: "Booking Flow — Round Selection",
    defaultValue: "Select the tee time for Round {round}",
  },
  "booking.roundHeaderWithCourse": {
    label: "Active round bar — after a club is picked",
    group: "Booking Flow — Round Selection",
    defaultValue: "Round {round}: {course}",
  },
  "booking.roundDateTrigger": {
    label: "Date picker — placeholder",
    group: "Booking Flow — Round Selection",
    defaultValue: "Round {round} date",
  },
  "booking.roundTeeTimeTrigger": {
    label: "Tee time picker — placeholder",
    group: "Booking Flow — Round Selection",
    defaultValue: "Round {round} tee time",
  },
  "booking.pickDateToUnlock": {
    label: "Locked-clubs banner",
    group: "Booking Flow — Round Selection",
    defaultValue: "Pick a date for Round {round} to unlock these clubs",
  },
  "booking.noClubsAssigned": {
    label: "Empty state — no clubs for this round",
    group: "Booking Flow — Round Selection",
    defaultValue: "No golf clubs assigned to this round yet.",
  },
  "booking.clubCardLocked": {
    label: "Club card button — locked",
    group: "Booking Flow — Round Selection",
    defaultValue: "Locked",
  },
  "booking.clubCardSelected": {
    label: "Club card button — selected",
    group: "Booking Flow — Round Selection",
    defaultValue: "Selected",
  },
  "booking.clubCardSelect": {
    label: "Club card button — select",
    group: "Booking Flow — Round Selection",
    defaultValue: "Select",
  },

  // --- Checkout / Review & Pay (src/components/packages/PackageCheckoutView.jsx) ---
  "checkout.title": {
    label: "Page title",
    group: "Checkout — Review & Pay",
    defaultValue: "Review & Pay",
  },
  "checkout.subtitle": {
    label: "Page subtitle",
    group: "Checkout — Review & Pay",
    defaultValue: "Confirm your package details before payment",
  },
  "checkout.sectionGolfRounds": {
    label: "Section label — Golf Rounds",
    group: "Checkout — Review & Pay",
    defaultValue: "Golf Rounds",
  },
  "checkout.sectionFerry": {
    label: "Section label — Ferry",
    group: "Checkout — Review & Pay",
    defaultValue: "Ferry (Round Trip)",
  },
  "checkout.sectionLandTransport": {
    label: "Section label — Land Transport",
    group: "Checkout — Review & Pay",
    defaultValue: "Land Transport",
  },
  "checkout.sectionAccommodation": {
    label: "Section label — Accommodation",
    group: "Checkout — Review & Pay",
    defaultValue: "Accommodation",
  },
  "checkout.couponHeading": {
    label: "Coupon box — heading",
    group: "Checkout — Review & Pay",
    defaultValue: "Apply Coupon",
  },
  "checkout.couponSubtext": {
    label: "Coupon box — subtext",
    group: "Checkout — Review & Pay",
    defaultValue: "Have a promo code? Apply it here",
  },
  "checkout.couponPlaceholder": {
    label: "Coupon input placeholder",
    group: "Checkout — Review & Pay",
    defaultValue: "Enter coupon code",
  },
  "checkout.couponApplyButton": {
    label: "Coupon apply button",
    group: "Checkout — Review & Pay",
    defaultValue: "Apply",
  },
  "checkout.couponHelpText": {
    label: "Coupon box — help text",
    group: "Checkout — Review & Pay",
    defaultValue: "Enter a valid promotional code to get a discount.",
  },
  "checkout.summaryLandTransportLabel": {
    label: "Price summary — land transport line",
    group: "Checkout — Review & Pay",
    defaultValue: "Land Transport (inbound + return)",
  },
  "checkout.totalLabel": {
    label: "Total label",
    group: "Checkout — Review & Pay",
    defaultValue: "Total",
  },
  "checkout.preparingBooking": {
    label: "Preparing-booking loading message",
    group: "Checkout — Review & Pay",
    defaultValue: "Preparing your booking…",
  },
  "checkout.errorBookingStart": {
    label: "Error — booking draft failed to start",
    group: "Checkout — Review & Pay",
    defaultValue: "Failed to start your booking. Please try again.",
  },
  "checkout.errorPaymentFailed": {
    label: "Error — payment failed",
    group: "Checkout — Review & Pay",
    defaultValue: "Payment failed. Please try again.",
  },

  // --- 404 page (src/lib/PageNotFound.jsx) ---
  "notFound.title": {
    label: "Title",
    group: "404 Page",
    defaultValue: "Page Not Found",
  },
  "notFound.message": {
    label: "Message — \"{page}\" is filled in with the broken URL",
    group: "404 Page",
    defaultValue: 'The page "{page}" could not be found in this application.',
  },
  "notFound.homeButton": {
    label: "Home button",
    group: "404 Page",
    defaultValue: "Go Home",
  },

  // --- Booking confirmation (src/components/packages/PackageConfirmationView.jsx) ---
  "confirmation.heading": {
    label: "Heading",
    group: "Booking Confirmation",
    defaultValue: "Booking Confirmed!",
  },
  "confirmation.subtext": {
    label: "Subtext",
    group: "Booking Confirmation",
    defaultValue: "Your golf package has been booked successfully.",
  },
  "confirmation.reservationLabel": {
    label: "Reservation number label",
    group: "Booking Confirmation",
    defaultValue: "Reservation",
  },
  "confirmation.sectionPackage": {
    label: "Section label — Package",
    group: "Booking Confirmation",
    defaultValue: "Package",
  },
  "confirmation.sectionGolfRounds": {
    label: "Section label — Golf Rounds",
    group: "Booking Confirmation",
    defaultValue: "Golf Rounds",
  },
  "confirmation.sectionFerry": {
    label: "Section label — Ferry",
    group: "Booking Confirmation",
    defaultValue: "Ferry (Round Trip)",
  },
  "confirmation.sectionLandTransport": {
    label: "Section label — Land Transport",
    group: "Booking Confirmation",
    defaultValue: "Land Transport",
  },
  "confirmation.sectionAccommodation": {
    label: "Section label — Accommodation",
    group: "Booking Confirmation",
    defaultValue: "Accommodation",
  },
  "confirmation.totalPaidLabel": {
    label: "Total paid label",
    group: "Booking Confirmation",
    defaultValue: "Total Paid",
  },
  "confirmation.paymentMethodLabel": {
    label: "Payment method label",
    group: "Booking Confirmation",
    defaultValue: "Payment Method",
  },
  "confirmation.homeButton": {
    label: "Home button",
    group: "Booking Confirmation",
    defaultValue: "Back to Home",
  },
  "confirmation.emailNote": {
    label: "Email delivery note",
    group: "Booking Confirmation",
    defaultValue:
      "You will receive your Exqura Golf Experience Pass via email within 48 hours. Please check your Spam/Junk inbox if you are unable to locate it.",
  },

  // --- My Bookings (src/pages/MyBookings.jsx) ---
  "myBookings.title": {
    label: "Page title",
    group: "My Bookings",
    defaultValue: "My Bookings",
  },
  "myBookings.filtersHeading": {
    label: "Filters card — heading",
    group: "My Bookings",
    defaultValue: "Filters",
  },
  "myBookings.allDatesPlaceholder": {
    label: "Date filter — placeholder",
    group: "My Bookings",
    defaultValue: "All Dates",
  },
  "myBookings.clearFiltersButton": {
    label: "Clear filters button",
    group: "My Bookings",
    defaultValue: "Clear Filters",
  },
  "myBookings.emptyTitle": {
    label: "Empty state — title",
    group: "My Bookings",
    defaultValue: "No bookings found",
  },
  "myBookings.emptySubtext": {
    label: "Empty state — subtext",
    group: "My Bookings",
    defaultValue: "Try adjusting your filters or make a new booking",
  },
  "myBookings.cancelConfirm": {
    label: "Cancel-booking confirm prompt",
    group: "My Bookings",
    defaultValue: "Are you sure you want to cancel this booking?",
  },
};

/** Fills "{token}" placeholders in a CMS-editable template string — e.g.
 * fillTemplate(t.stepSelectDate, { round: 2 }) turns
 * "Select a date for Round {round}" into "Select a date for Round 2".
 * Needed because useSiteText()/useSiteTexts() only ever return the raw
 * admin-edited string — any registry entry whose copy needs a dynamic
 * value spliced in (a round number, a course name) documents its
 * token(s) in a comment above it and is run through this before display. */
export function fillTemplate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  );
}

/** TEXT_REGISTRY entries grouped for the admin editor, in registration
 * order (both across groups and within one) — no separate sort step, so
 * ordering here is exactly the ordering an admin sees. */
export function groupedTextRegistry() {
  const groups = new Map();
  for (const [key, def] of Object.entries(TEXT_REGISTRY)) {
    const groupName = def.group || "Other";
    if (!groups.has(groupName)) groups.set(groupName, []);
    groups.get(groupName).push({ key, ...def });
  }
  return Array.from(groups.entries()).map(([group, items]) => ({
    group,
    items,
  }));
}
